import { PlausibleEvent } from 'enums/plausible'
import { useEffect, useMemo } from 'react'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'

import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import AuthLayout from 'layouts/AuthLayout'

import LoginFormOrGoogle from 'components/LoginFormOrGoogle/LoginFormOrGoogle'
import StructuredData from 'components/StructuredData/StructuredData'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const SigninPage = ({
  referrer,
  ssrHost,
}: {
  referrer: string
  ssrHost: string
}) => {
  const hostName = process.env.NEXT_PUBLIC_BN_HOSTNAME === ssrHost ? 'Binany24' : 'Binany'
  const domainName = useMemo(
    () => (ssrHost ? ssrHost.split('.')[ssrHost.split('.').length - 1] : 'com'),
    [ssrHost],
  )

  const setEvent = usePlausible()

  useEffect(
    () =>
      setEvent(PlausibleEvent.OpenedSignIn, {
        step: 1,
        referrer,
      }),
    [],
  )

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }

  return (
    <>
      <StructuredData host={ssrHost} variant="base" path="/auth/signin" />
      <Head>
        <title>
          Binany login | {hostName}.{domainName} sign in
        </title>
        <meta
          name="description"
          content={`Log in to the ${hostName} trading platform. Sign in and start trading!`}
        />

        <meta property={`og:title" content="Binany login | ${hostName}.${domainName} sign in`} />
        <meta
          property="og:description"
          content={`Log in to the ${hostName} trading platform. Sign in and start trading!`}
        />
        <meta property="og:image" content={`https://${ssrHost}/images/ogImages/authSignin.png`} />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://${ssrHost}/auth/signin`} />
      </Head>
      <AuthLayout>
        <LoginFormOrGoogle referrer={referrer} />
      </AuthLayout>
    </>
  )
}

export default SigninPage

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      ssrHost: req.headers.host || '',
      ...(await serverSideTranslations(locale, ['main', 'contacts', 'shared', 'error'])),
    },
  }
}
